import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginPageComponent } from '@dges/modules/auth/feature';
import {
  canActivate,
  loggedIn,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import {
  CompaniesFacade,
  EducationFacade,
  JobsFacade,
  ProjectsFacade,
} from '@dges/modules/portfolio/store/ngrx';
import { ENTITY_FACADE, EntityFacade } from '@dges/pattern/entity-list';
import { Company, Education, Job, Project } from '@dges/modules/portfolio/domain';

const adminOnly = () => loggedIn;
const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);

const projectsEntityFacade = {
  provide: ENTITY_FACADE,
  useFactory: (f: ProjectsFacade): EntityFacade<Project> => ({
    loaded$: f.loaded$,
    allEntities$: f.allItems$,
    selectedEntity$: f.selectedItem$,
    load: () => f.load(),
    addEntity: (p) => f.addItem(p),
    editEntity: (p) => f.editItem(p),
    deleteEntity: (p) => f.deleteItem(p),
    editOrAddSuccess: () => f.editOrAddSuccess(),
  }),
  deps: [ProjectsFacade],
};

const jobsEntityFacade = {
  provide: ENTITY_FACADE,
  useFactory: (f: JobsFacade): EntityFacade<Job> => ({
    loaded$: f.loaded$,
    allEntities$: f.allItems$,
    selectedEntity$: f.selectedItem$,
    load: () => f.load(),
    addEntity: (j) => f.addItem(j),
    editEntity: (j) => f.editItem(j),
    deleteEntity: (j) => f.deleteItem(j),
    editOrAddSuccess: () => f.editOrAddSuccess(),
  }),
  deps: [JobsFacade],
};

const educationsEntityFacade = {
  provide: ENTITY_FACADE,
  useFactory: (f: EducationFacade): EntityFacade<Education> => ({
    loaded$: f.loaded$,
    allEntities$: f.allItems$,
    selectedEntity$: f.selectedItem$,
    load: () => f.load(),
    addEntity: (e) => f.addItem(e),
    editEntity: (e) => f.editItem(e),
    deleteEntity: (e) => f.deleteItem(e),
    editOrAddSuccess: () => f.editOrAddSuccess(),
  }),
  deps: [EducationFacade],
};

const companiesEntityFacade = {
  provide: ENTITY_FACADE,
  useFactory: (f: CompaniesFacade): EntityFacade<Company> => ({
    loaded$: f.loaded$,
    allEntities$: f.allCompanies$,
    load: () => {
      f.init();
      return f.allCompanies$;
    },
    addEntity: (c) => f.addCompany(c),
    editEntity: (c) => f.editCompany(c),
    deleteEntity: (c) => f.deleteCompany(c),
    editOrAddSuccess: () => f.editOrAddSuccess(),
  }),
  deps: [CompaniesFacade],
};

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'projects',
  },
  {
    path: 'projects',
    ...canActivate(adminOnly),
    ...canActivate(redirectUnauthorizedToLogin),
    providers: [projectsEntityFacade],
    loadChildren: () =>
      import('@dges/modules/portfolio/featureReadWrite').then(
        (m) => m.projectsEditableRoutes,
      ),
  },
  {
    path: 'jobs',
    ...canActivate(adminOnly),
    ...canActivate(redirectUnauthorizedToLogin),
    providers: [jobsEntityFacade],
    loadChildren: () =>
      import('@dges/modules/portfolio/featureReadWrite').then(
        (m) => m.jobsEditableRoutes,
      ),
  },
  {
    path: 'educations',
    ...canActivate(adminOnly),
    ...canActivate(redirectUnauthorizedToLogin),
    providers: [educationsEntityFacade],
    loadChildren: () =>
      import('@dges/modules/portfolio/featureReadWrite').then(
        (m) => m.educationsEditableRoutes,
      ),
  },
  {
    path: 'skills-panel',
    ...canActivate(adminOnly),
    ...canActivate(redirectUnauthorizedToLogin),
    loadComponent: () =>
      import('@dges/modules/portfolio/featureReadWrite').then(
        (m) => m.SkillsControlPanelComponent,
      ),
  },
  {
    path: 'companies',
    ...canActivate(adminOnly),
    ...canActivate(redirectUnauthorizedToLogin),
    providers: [companiesEntityFacade],
    loadChildren: () =>
      import('@dges/modules/portfolio/featureReadWrite').then(
        (m) => m.companiesEditableRoutes,
      ),
  },
  {
    path: 'login',
    component: LoginPageComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
